/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeCheckbox,
  DimeField,
  DimeFieldContent,
  DimeFieldDescription,
  DimeFieldError,
  DimeFieldGroup,
  DimeFieldLabel,
  DimeFieldLegend,
  DimeFieldSeparator,
  DimeFieldSet,
  DimeFieldTitle,
  DimeInput,
  DimeSeparator,
  DimeTextarea,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/field')({
  component: FieldDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex items-start rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

// ── Input example ──────────────────────────────────────────────────────────────

function FieldInput() {
  return (
    <DimeFieldSet className="w-full max-w-xs">
      <DimeFieldLegend>Account</DimeFieldLegend>
      <DimeFieldGroup>
        <DimeField>
          <DimeFieldLabel htmlFor="input-username">Username</DimeFieldLabel>
          <DimeInput id="input-username" type="text" placeholder="Test User" />
          <DimeFieldDescription>Choose a unique username for your account.</DimeFieldDescription>
        </DimeField>
        <DimeField>
          <DimeFieldLabel htmlFor="input-password">Password</DimeFieldLabel>
          <DimeFieldDescription>Must be at least 8 characters long.</DimeFieldDescription>
          <DimeInput id="input-password" type="password" placeholder="••••••••" />
        </DimeField>
      </DimeFieldGroup>
    </DimeFieldSet>
  );
}

// ── Textarea example ───────────────────────────────────────────────────────────

function FieldTextarea() {
  return (
    <DimeFieldSet className="w-full max-w-xs">
      <DimeFieldLegend>Feedback</DimeFieldLegend>
      <DimeFieldGroup>
        <DimeField>
          <DimeFieldLabel htmlFor="textarea-feedback">Feedback</DimeFieldLabel>
          <DimeTextarea
            id="textarea-feedback"
            placeholder="Your feedback helps us improve..."
            rows={3}
          />
          <DimeFieldDescription>Share your thoughts about our service.</DimeFieldDescription>
        </DimeField>
      </DimeFieldGroup>
    </DimeFieldSet>
  );
}

// ── Coming soon placeholders ───────────────────────────────────────────────────

function ComingSoon({ label }: Readonly<{ label: string }>) {
  return (
    <div className="flex w-full max-w-xs flex-col gap-1.5">
      <p className="text-sm font-medium text-foreground">{label}</p>
      <div className="flex h-9 w-full items-center rounded-md border border-dashed border-border px-3 text-sm text-muted-foreground">
        Coming soon
      </div>
    </div>
  );
}

// ── Fieldset example ───────────────────────────────────────────────────────────

function FieldFieldset() {
  return (
    <DimeFieldSet className="w-full max-w-sm">
      <DimeFieldLegend>Address Information</DimeFieldLegend>
      <DimeFieldDescription>We need your address to deliver your order.</DimeFieldDescription>
      <DimeFieldGroup>
        <DimeField>
          <DimeFieldLabel htmlFor="fs-street">Street Address</DimeFieldLabel>
          <DimeInput id="fs-street" type="text" placeholder="123 Main St" />
        </DimeField>
        <div className="grid grid-cols-2 gap-4">
          <DimeField>
            <DimeFieldLabel htmlFor="fs-city">City</DimeFieldLabel>
            <DimeInput id="fs-city" type="text" placeholder="New York" />
          </DimeField>
          <DimeField>
            <DimeFieldLabel htmlFor="fs-zip">Postal Code</DimeFieldLabel>
            <DimeInput id="fs-zip" type="text" placeholder="90502" />
          </DimeField>
        </div>
      </DimeFieldGroup>
    </DimeFieldSet>
  );
}

// ── Checkbox example ───────────────────────────────────────────────────────────

function FieldCheckbox() {
  return (
    <DimeFieldSet className="w-full max-w-sm">
      <DimeFieldLegend>Show these items on the desktop</DimeFieldLegend>
      <DimeFieldDescription>Select the items you want to show on the desktop.</DimeFieldDescription>
      <DimeFieldGroup>
        {[
          { id: 'cb-connected', label: 'Connected Servers' },
          { id: 'cb-hard-disks', label: 'Hard Disks' },
          { id: 'cb-external', label: 'External Disks' },
          { id: 'cb-cds', label: 'CDs, DVDs and iPods' },
        ].map(({ id, label }) => (
          <DimeField key={id} orientation="horizontal">
            <DimeCheckbox id={id} />
            <DimeFieldLabel htmlFor={id}>{label}</DimeFieldLabel>
          </DimeField>
        ))}
        <DimeFieldSeparator />
        <DimeFieldDescription>
          Your Desktop &amp; Documents folders are being synced with iCloud Drive. You can access
          them from other devices.
        </DimeFieldDescription>
      </DimeFieldGroup>
    </DimeFieldSet>
  );
}

// ── Choice Card example ────────────────────────────────────────────────────────

const COMPUTE_OPTIONS = [
  {
    id: 'cpu',
    title: 'CPU',
    description: 'Standard compute for general workloads.',
  },
  {
    id: 'gpu',
    title: 'GPU',
    description: 'Accelerated compute for ML and graphics.',
  },
] as const;

function FieldChoiceCard() {
  const [selected, setSelected] = React.useState<string>('cpu');

  return (
    <DimeFieldSet className="w-full max-w-sm">
      <DimeFieldLegend>Compute Environment</DimeFieldLegend>
      <DimeFieldDescription>Select the compute environment for your cluster.</DimeFieldDescription>
      <DimeFieldGroup>
        {COMPUTE_OPTIONS.map(({ id, title, description }) => (
          <DimeFieldLabel
            key={id}
            htmlFor={id}
            className={[
              'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
              selected === id
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/40',
            ].join(' ')}
          >
            <DimeCheckbox
              id={id}
              checked={selected === id}
              onCheckedChange={() => setSelected(id)}
              className="mt-0.5"
            />
            <DimeFieldContent>
              <DimeFieldTitle>{title}</DimeFieldTitle>
              <DimeFieldDescription>{description}</DimeFieldDescription>
            </DimeFieldContent>
          </DimeFieldLabel>
        ))}
      </DimeFieldGroup>
    </DimeFieldSet>
  );
}

// ── Field Group with separator ─────────────────────────────────────────────────

function FieldGroupExample() {
  return (
    <DimeFieldSet className="w-full max-w-sm">
      <DimeFieldLegend>Notification preferences</DimeFieldLegend>
      <DimeFieldGroup className="divide-y divide-border rounded-lg border">
        <DimeField orientation="horizontal" className="items-start p-4">
          <DimeCheckbox id="fg-chatgpt" className="mt-0.5" />
          <DimeFieldContent className="ml-3">
            <DimeFieldLabel htmlFor="fg-chatgpt">ChatGPT responses</DimeFieldLabel>
            <DimeFieldDescription>
              Get notified when ChatGPT responds to requests that take time, like research or image
              generation.
            </DimeFieldDescription>
          </DimeFieldContent>
        </DimeField>
        <DimeField orientation="horizontal" className="items-start p-4">
          <DimeCheckbox id="fg-tasks" className="mt-0.5" />
          <DimeFieldContent className="ml-3">
            <DimeFieldLabel htmlFor="fg-tasks">Task updates</DimeFieldLabel>
            <DimeFieldDescription>
              Get notified when tasks you've created have updates. Manage tasks
            </DimeFieldDescription>
          </DimeFieldContent>
        </DimeField>
      </DimeFieldGroup>
    </DimeFieldSet>
  );
}

// ── Validation and Errors example ─────────────────────────────────────────────

function FieldValidation() {
  const [email, setEmail] = React.useState('');
  const isInvalid = email.length > 0 && !email.includes('@');

  return (
    <DimeFieldSet className="w-full max-w-xs">
      <DimeFieldLegend>Validation</DimeFieldLegend>
      <DimeFieldGroup>
        <DimeField data-invalid={isInvalid || undefined}>
          <DimeFieldLabel htmlFor="val-email">Email</DimeFieldLabel>
          <DimeInput
            id="val-email"
            type="email"
            aria-invalid={isInvalid}
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder="you@example.com"
          />
          {isInvalid && <DimeFieldError>Enter a valid email address.</DimeFieldError>}
        </DimeField>
        <DimeField data-invalid>
          <DimeFieldLabel htmlFor="val-username">Username</DimeFieldLabel>
          <DimeInput id="val-username" aria-invalid placeholder="testuser" />
          <DimeFieldError>Choose another username.</DimeFieldError>
        </DimeField>
      </DimeFieldGroup>
    </DimeFieldSet>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

function FieldDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Field</h1>
      <p className="mb-8 text-muted-foreground">
        Composable form field primitives for building accessible forms.
      </p>

      <DocsSection title="Input">
        <Preview>
          <FieldInput />
        </Preview>
      </DocsSection>

      <DocsSection title="Textarea">
        <Preview>
          <FieldTextarea />
        </Preview>
      </DocsSection>

      <DocsSection title="Select">
        <Preview>
          <ComingSoon label="Department" />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Waiting on <code className="font-mono">DimeAutocomplete</code> (combobox).
        </p>
      </DocsSection>

      <DocsSection title="Slider">
        <Preview>
          <ComingSoon label="Price Range" />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Waiting on <code className="font-mono">DimeSlider</code>.
        </p>
      </DocsSection>

      <DocsSection title="Switch">
        <Preview>
          <ComingSoon label="Subscribe to the newsletter" />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Waiting on <code className="font-mono">DimeSwitch</code>.
        </p>
      </DocsSection>

      <DocsSection title="Radio">
        <Preview>
          <ComingSoon label="Subscription Plan" />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Waiting on <code className="font-mono">DimeRadioGroup</code> /{' '}
          <code className="font-mono">DimeRadioItem</code>.
        </p>
      </DocsSection>

      <DocsSection title="Fieldset">
        <Preview>
          <FieldFieldset />
        </Preview>
      </DocsSection>

      <DocsSection title="Checkbox">
        <Preview>
          <FieldCheckbox />
        </Preview>
      </DocsSection>

      <DocsSection title="Choice Card">
        <Preview>
          <FieldChoiceCard />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap <code className="font-mono">DimeField</code> components inside{' '}
          <code className="font-mono">DimeFieldLabel</code> to create selectable card groups. Uses{' '}
          <code className="font-mono">DimeFieldContent</code> and{' '}
          <code className="font-mono">DimeFieldTitle</code> to keep descriptions aligned alongside
          the control.
        </p>
      </DocsSection>

      <DocsSection title="Field Group">
        <Preview>
          <FieldGroupExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Stack <code className="font-mono">DimeField</code> components with{' '}
          <code className="font-mono">DimeFieldGroup</code>. Use{' '}
          <code className="font-mono">DimeFieldSeparator</code> or Tailwind{' '}
          <code className="font-mono">divide-y</code> to divide sections.
        </p>
      </DocsSection>

      <DocsSection title="Validation and Errors">
        <Preview>
          <FieldValidation />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Add <code className="font-mono">data-invalid</code> to{' '}
          <code className="font-mono">DimeField</code> to switch the block into an error state. Set{' '}
          <code className="font-mono">aria-invalid</code> on the input for assistive technologies.
          Render <code className="font-mono">DimeFieldError</code> immediately after the control.
        </p>
      </DocsSection>
    </div>
  );
}
